import { Vue, Component } from 'vue-property-decorator';
import moment from 'moment';

import APIService from '../../services/APIService';
import GradeResponse from '../../models/responses/GradeResponse';
import SubjectDetailResponse from '../../models/responses/SubjectDetailResponse';
import GradeCard from '../../components/GradeCard/GradeCard.vue';
import GradeForm from '../../components/Forms/Grade/GradeForm.vue';
import SemesterSubjectForm from '../../components/Forms/SemesterSubject/SemesterSubjectForm.vue';
import WishGrade from '../../components/Forms/WishGrade/WishGrade.vue';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner.vue';

/**
 * Detail view of a subject
 */
@Component({
  name: 'SubjectDetailView',
  components: {
    GradeCard,
    GradeForm,
    SemesterSubjectForm,
    WishGrade,
    LoadingSpinner,
  },
})
export default class SubjectDetailView extends Vue {
  private subject: SubjectDetailResponse | null = null;
  private loading = false;

  get isCoach(): boolean {
    return this.$store.getters.isCoach;
  }

  get isApprentice(): boolean {
    return this.$store.getters.isApprentice;
  }

  get isGradeFormOpen(): boolean {
    return this.$store.state.isGradeFormOpen;
  }

  get isSemesterSubjectFormOpen(): boolean {
    return this.$store.state.isSemesterSubjectFormOpen;
  }

  get isWishGradeFormOpen(): boolean {
    return this.$store.state.isWishGradeFormOpen;
  }

  /**
   * Fetches subject data from the backend.
   */
  async mounted() {
    await this.getSubject();

    this.$store.commit('setSelectedSubject', this.subject);

    let nav;
    if (this.isApprentice && this.subject)
      nav = {
        route: `/dashboard?semester=${this.subject.semester.id}`,
        textKey: 'general.dashboard',
      };
    if (this.isCoach && this.subject)
      nav = {
        route: `/apprentice-detail/${this.subject.user.id}?semester=${this.subject.semester.id}`,
        text: `${this.subject.user.firstName} ${this.subject.user.lastName}`,
      };
    this.$store.commit('setBackNavigation', nav);
  }

  /**
   * Fetches subject data from the backend.
   *
   * @returns subject detail information
   */
  private async getSubject(): Promise<void> {
    this.loading = true;
    this.subject = await APIService.getSubject(this.$route.params.id);
    this.loading = false;
  }

  /**
   * Resets the local state and fetches the subject detail information.
   */
  private reloadSubjectDetail(): void {
    this.subject = null;
    this.getSubject();
  }

  /**
   * Opens grade form in create mode.
   */
  private openGradeForm(): void {
    this.$store.commit('setEditGrade', null);
    this.$store.commit('toggleGradeForm');
  }

  /**
   * Opens subject form in edit mode.
   */
  private openSubjEditForm(): void {
    this.$store.commit('setEditSubject', this.subject);
    this.$store.commit('toggleSemesterSubjectForm');
  }

  /**
   * Opens the wish grade form.
   */
  private openWishGradeForm(): void {
    this.$store.commit('toggleWishGradeForm');
  }

  /**
   * Opens the grade form in edit mode.
   *
   * @param grade - grade that should be edited
   */
  private openEditGradeForm(grade: GradeResponse): void {
    this.$store.commit('setEditGrade', grade);
    this.$store.commit('toggleGradeForm');
  }

  /**
   * Deletes a grade.
   *
   * @param gradeId - id of grade to be deleted
   */
  private async deleteGrade(gradeId: string): Promise<void> {
    await APIService.deleteGrade(gradeId);
    this.reloadSubjectDetail();
  }

  /**
   * Formats the given date.
   *
   * @param date which should be formatted
   * @returns the formatted date string
   */
  private formatDate(date: string) {
    if (!date) return;
    return moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY');
  }

  /**
   * Opens the confirmation modal for deleting a grade.
   *
   * @param id of grade to be deleted
   */
  private openConfirmation(id: string) {
    this.$store.commit('openModal', {
      title: this.$t('subjectDetail.gradeCard.delete'),
      text: this.$t('general.confirmation'),
      confirmationCallback: () => this.deleteGrade(id),
    });
  }
}
